import React, { useState, useMemo, useCallback } from 'react';
import { Box, Collapse, Divider, List, ListItem, ListItemButton, ListItemText, Paper, useTheme } from '@mui/material';
import { ExpandLess, ExpandMore, KeyboardArrowDown } from '@mui/icons-material';

import { useAppSelector } from '../../app/hooks';
import { AssessmentSurvey } from '../../interfaces/assessment.interface';
import { assessmentSteps } from '../../services/surveyHelpers';
import SurveyAssessmentProgress from './SurveyAssessmentProgress';
import { Workflow } from '../../interfaces/workflow.interface';
import WorkflowStatus, { getWorkflowStatusForStep } from '../Directory/WorkflowStatus';
import { Company } from '../../interfaces/users.interface';

type ProgressCardProps = {
  mt?: number;
  workflow?: Workflow;
  onProgressBarClick: () => void;
  assessmentSurvey?: AssessmentSurvey;
  company: Company
};

export default function ProgressCard({ mt, onProgressBarClick, workflow, assessmentSurvey, company }: ProgressCardProps) {
  const theme = useTheme();
  const { survey } = useAppSelector((state) => state.surveys);
  const [expanded, setExpanded] = useState(false);

  const currentStep = useMemo(() => {
    if (!workflow) return 0;

    // Default to displayStatus, fallback to getWorkflowStatusForStep
    const currentStatus = workflow.displayStatus?.status || getWorkflowStatusForStep({ workflow, company, survey, assessmentSurvey });
    const index = assessmentSteps.findIndex(step => step === currentStatus);
    return index === -1 ? 0 : index;
  }, [workflow, company, survey, assessmentSurvey, assessmentSteps]);

  const typographyProps = useCallback((index: number) => {
    let color = '#ccc';
    if (index === currentStep) {
      color = theme.palette.primary.main;
    }
    if (index < currentStep) color = theme.palette.success.main;

    return { color, fontWeight: 'bold', textAlign: 'center' };
  }, [currentStep, theme.palette.primary.main, theme.palette.success.main]);

  return (
    <Paper sx={{ mt: mt || 0, mb: 1 }}>
      <List>
        <ListItemButton onClick={() => setExpanded(!expanded)}>
        <ListItemText 
            primary={
              <Box style={{ color: theme.palette.customThemeColors?.darkGrey, display: 'flex' }}>
                <Box style={{ fontWeight: 800, marginRight: 6 }}>Current Step: </Box>
                  <WorkflowStatus
                  workflowDisplayStatusObject={workflow?.displayStatus}
                  hideProgressBar
                  hideRemediationCounts
                />
              </Box>
            }
          />
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        {!expanded && <SurveyAssessmentProgress onClick={onProgressBarClick} />}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List disablePadding>
        {assessmentSteps.map((step, x) => (
          <div key={`${step}-${x}`}>
            <ListItem>
              <ListItemText
                primary={step}
                primaryTypographyProps={{ sx: typographyProps(x) }}
              />
            </ListItem>
            {x !== assessmentSteps.length - 1 && (
              <Divider>
                <KeyboardArrowDown sx={{ transform: 'translateY(5px)' }} />
              </Divider>
            )}
          </div>
        ))}
        </List>
      </Collapse>
        {expanded && <SurveyAssessmentProgress onClick={onProgressBarClick} />}
      </List>
    </Paper>
  );
}
